import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown, Tag, ArrowRight, Headphones, Ruler } from 'lucide-react';
import { Button } from './ui/button';
import CartButton from './CartButton';
import { isShopPath } from '../lib/productPath';
import { track, FUNNEL } from '../lib/track';

const WHATSAPP_URL = 'https://wa.me/message/X4OR7NTM36W5M1';

// Help on shop pages, on phones: a quiet icon in the header instead of the
// floating call and WhatsApp buttons. Calling is there for those who want
// it, without pulling buyers away from buying online.
function HelpMenu({ onCall }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const row = 'flex h-11 items-center gap-2.5 rounded-lg border border-gray-200 px-3 text-sm font-semibold text-gray-900 hover:border-blue-300';
  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-haspopup="true"
        aria-label="Help: call or WhatsApp us"
        className={`flex h-11 w-11 flex-col items-center justify-center rounded-lg text-gray-600 ${open ? 'bg-gray-100 text-gray-900' : ''}`}>
        <Headphones size={19} strokeWidth={1.8} aria-hidden="true" />
        <span className="text-[9px] font-semibold leading-none">Help</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-64 rounded-xl border border-gray-200 bg-white p-3 shadow-xl">
          <p className="text-sm font-bold text-gray-900">Need help choosing?</p>
          <p className="mt-0.5 text-xs text-gray-600">Talk to our sales team.</p>
          <div className="mt-3 flex flex-col gap-2">
            <a href="tel:+917703900307" onClick={(e) => { track(FUNNEL.PHONE_CLICK, { source: 'header_help' }); onCall(e); }} className={row}>
              <Phone size={17} className="text-blue-700" aria-hidden="true" /> Call 77039 00307
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
              onClick={() => track(FUNNEL.WHATSAPP_HANDOFF, { source: 'header_help' })} className={row}>
              <svg viewBox="0 0 24 24" className="h-[17px] w-[17px] text-green-700" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 11.5a8.4 8.4 0 0 1-12.4 7.4L3 21l2.1-5.5A8.4 8.4 0 1 1 21 11.5z" />
              </svg>
              WhatsApp us
            </a>
            <Link to="/ac-calculator" className={row}>
              <Ruler size={17} className="text-blue-700" aria-hidden="true" /> AC size calculator
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

// The two corners on the main bar (Saheb, 10 Oct 2026): one for people
// buying for their own home or office, one for architects and designers.
// Architect Corner is highlighted. A `section` scrolls the homepage; a `to`
// with a hash opens that chapter of the page.
const CUSTOMER_CORNER = [
  { label: 'AC Calculator', to: '/ac-calculator', text: 'The right tonnage for your room' },
  { label: 'Compare ACs', to: '/compare-ac', text: 'Any ACs side by side, with one clear answer' },
  { label: 'AC Guides', to: '/blog', text: 'Inverter or not, and other AC questions' },
  { label: 'HVAC Design', to: '/hvac-consultant', text: '₹99 video call, site visit, heat load' },
  { label: 'Services', section: 'services', text: 'Installation, AMC and repairs' },
  { label: 'Clearance sale', to: '/clearance-ac-sale', text: 'Sealed-box ACs at clearance prices' },
];
// Buy AC opens the shop on click and, on hover, the ways to choose one
// (Clarity, Oct 2026: shoppers looked for Compare under Buy, not under
// Customer Corner).
const BUY_MENU = [
  { label: 'All Daikin ACs', to: '/daikin-ac-price-list-delhi', text: 'Every split AC, prices with GST' },
  { label: 'Compare ACs', to: '/compare-ac', text: 'Two or three ACs side by side, feature by feature' },
  { label: 'AC size calculator', to: '/ac-calculator', text: 'The right tonnage for your room' },
  { label: 'Clearance sale', to: '/clearance-ac-sale', text: 'Sealed-box ACs at clearance prices' },
];
const ARCHITECT_CORNER = [
  { label: 'Indoor unit corner', to: '/architects-interior-designers#units', text: 'All 15 Daikin VRV indoor units' },
  { label: 'Ceiling space and checker', to: '/architects-interior-designers#ceiling', text: 'How low the ceiling can go' },
  { label: 'Pipe and drain space', to: '/architects-interior-designers#pipes', text: 'How much room the pipes need' },
  { label: 'The perfect VRV installation', to: '/architects-interior-designers#installation', text: '12 things we check on every job' },
  { label: 'HVAC design and heat load', to: '/hvac-consultant?service=design&from=architect-corner', text: 'Drawings, BOQ and heat load' },
  { label: 'VRV experience centre', to: '/daikin-vrv-systems', text: 'See and hear VRV working' },
];
const PARTNER_LINK = '/architects-interior-designers#partner';

// Opens a corner link. A link to a chapter of the page you are already on
// scrolls there (the router does not scroll for a hash change).
function useCornerGo(onSection, after) {
  const location = useLocation();
  return (e, item) => {
    if (item.section) { e.preventDefault(); onSection(item.section); }
    else {
      const [path, hash] = item.to.split('#');
      if (hash && location.pathname === path) {
        e.preventDefault();
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.history.replaceState(null, '', `${path}#${hash}`);
      }
    }
    if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
    if (after) after();
  };
}

function CornerMenu({ label, items, highlight, to, cta, onSection, labelClass }) {
  // Closes as soon as a link is clicked, even while the mouse is still over it.
  const [shut, setShut] = useState(false);
  const go = useCornerGo(onSection, () => setShut(true));
  const btn = labelClass || (highlight
    ? 'rounded-lg bg-blue-50 px-3 py-1.5 font-bold text-blue-800 ring-1 ring-blue-200 hover:bg-blue-100'
    : 'px-1 py-1.5 font-medium text-gray-700 hover:text-blue-600');
  const Label = to
    ? <Link to={to} className={`flex items-center gap-1 whitespace-nowrap transition ${btn}`} aria-haspopup="true">{label} <ChevronDown size={16} aria-hidden="true" /></Link>
    : <button type="button" className={`flex items-center gap-1 whitespace-nowrap transition ${btn}`} aria-haspopup="true">{label} <ChevronDown size={16} aria-hidden="true" /></button>;
  return (
    <div className="group relative" onMouseLeave={() => setShut(false)}>
      {Label}
      <div className={`invisible absolute left-0 top-full z-50 pt-2 opacity-0 transition-all duration-150 ${shut ? '' : 'group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100'}`}>
        <div className="w-80 rounded-xl border border-gray-100 bg-white p-2 shadow-xl">
          {items.map((it) => (
            <Link key={it.label} to={it.to || '/'} onClick={(e) => go(e, it)}
              className="block rounded-lg px-3 py-2.5 hover:bg-blue-50">
              <span className="block text-sm font-semibold text-gray-900">{it.label}</span>
              <span className="block text-xs text-gray-500">{it.text}</span>
            </Link>
          ))}
          {cta && (
            <Link to={cta.to} onClick={(e) => go(e, { to: cta.to })}
              className="mt-1 flex h-10 items-center justify-center rounded-lg bg-blue-700 text-sm font-bold text-white hover:bg-blue-800">
              {cta.label}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToSection = (sectionId) => {
    setMobileMenuOpen(false);
    if (location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // Desktop row: Buy AC · Customer Corner · Architect Corner · VRV · Explore.
  // The burger keeps Home and the homepage sections as well.
  const navItems = [
    { label: 'Home', id: 'home', desktopHidden: true },
    { label: 'Buy AC', id: 'prices', link: '/daikin-ac-price-list-delhi' },
    { label: 'VRV', id: 'vrv', link: '/daikin-vrv-systems' },
    { label: 'Projects', id: 'projects', desktopHidden: true },
    { label: 'About', id: 'about', desktopHidden: true },
    { label: 'Clients', id: 'clients', desktopHidden: true },
    { label: 'Contact', id: 'contact', link: '/contact', desktopHidden: true }
  ];
  const mobileGo = useCornerGo(scrollToSection, () => setMobileMenuOpen(false));

  const goHome = () => {
    setMobileMenuOpen(false);
    if (location.pathname !== "/") {
      window.location.href = "/";
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleCallClick = (e) => {
    e.preventDefault();
    if (window.gtagSendEvent) {
      window.gtagSendEvent('tel:+917703900307');
    } else {
      window.location.href = 'tel:+917703900307';
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-sm z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 lg:h-20">

          {/* Logo */}
          <div className="flex-shrink-0">
            <img
              src="/media/3pnsouhi-logo-final-20251216.webp"
              alt="Citi Cool Corporation"
              onClick={goHome}
              className="h-12 w-auto cursor-pointer hover:opacity-90 transition"
            />
          </div>

          {/* Desktop Navigation. Tighter gaps below 1280px so the row, with
              AC Calculator added, still fits a 1024px laptop. */}
          <nav className="hidden lg:flex space-x-3 xl:space-x-8 items-center">
            
            {/* Buy AC, the two corners, VRV */}
            {(() => {
              const linkItem = (item) => (
                <Link
                  key={item.id}
                  to={item.link}
                  aria-current={location.pathname === item.link ? 'page' : undefined}
                  className={`${item.id === 'prices' ? 'buy-ac-cta ' : ''}whitespace-nowrap rounded-md px-3 py-1.5 font-medium transition hover:text-blue-600 ${location.pathname === item.link ? 'text-blue-600' : 'text-gray-700'}`}
                >
                  {item.label}
                </Link>
              );
              const by = (id) => navItems.find((n) => n.id === id);
              return (
                <>
                  <CornerMenu label="Buy AC" items={BUY_MENU} to="/daikin-ac-price-list-delhi"
                    labelClass={`buy-ac-cta rounded-md px-3 py-1.5 font-medium hover:text-blue-600 ${location.pathname === '/daikin-ac-price-list-delhi' ? 'text-blue-600' : 'text-gray-700'}`}
                    onSection={scrollToSection} />
                  <CornerMenu label="Customer Corner" items={CUSTOMER_CORNER} onSection={scrollToSection} />
                  <CornerMenu label="Architect Corner" items={ARCHITECT_CORNER} highlight to="/architects-interior-designers"
                    cta={{ label: 'Become a partner', to: PARTNER_LINK }} onSection={scrollToSection} />
                  {linkItem(by('vrv'))}
                </>
              );
            })()}

            {/* SEO Mega Menu (Placed at the end) */}
            <div className="group relative">
              <button className="flex items-center gap-1 text-gray-700 hover:text-blue-600 font-medium py-2">
                Explore <ChevronDown size={16} />
              </button>
              
              {/* Mega Menu Dropdown */}
              <div className="absolute top-full right-0 w-[650px] bg-white border border-gray-100 shadow-xl rounded-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 flex flex-col">
                
                {/* Top Grid Section */}
                <div className="p-6 grid grid-cols-3 gap-6">
                  {/* Column 1: Brands */}
                  <div>
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Brands</h3>
                    <ul className="space-y-2">
                      <li><Link to="/daikin-dealer-delhi" className="text-sm text-gray-700 hover:text-blue-600 block">Daikin</Link></li>
                      <li><Link to="/o-general-dealer-delhi" className="text-sm text-gray-700 hover:text-blue-600 block">O-General</Link></li>
                      <li><Link to="#" className="text-sm text-gray-700 hover:text-blue-600 block">Mitsubishi Heavy</Link></li>
                      <li><Link to="#" className="text-sm text-gray-700 hover:text-blue-600 block">Hitachi</Link></li>
                    </ul>
                  </div>

                  {/* Column 2: Solutions */}
                  <div>
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Solutions</h3>
                    <ul className="space-y-2">
                      <li><Link to="/hvac-consultant" className="text-sm font-semibold text-blue-700 hover:text-blue-800 block">HVAC Design</Link></li>
                      <li><Link to="/architects-interior-designers" className="text-sm font-semibold text-blue-700 hover:text-blue-800 block">Architect Corner</Link></li>
                      <li><Link to="/vrv-ac-installation" className="text-sm text-gray-700 hover:text-blue-600 block">VRV / VRF</Link></li>
                      <li><Link to="/cassette-ac-installation" className="text-sm text-gray-700 hover:text-blue-600 block">Cassette AC</Link></li>
                      <li><Link to="/ductable-ac-installation" className="text-sm text-gray-700 hover:text-blue-600 block">Ductable AC</Link></li>
                      <li><Link to="/split-ac-installation" className="text-sm text-gray-700 hover:text-blue-600 block">Split AC</Link></li>
                      <li><Link to="/daikin-chiller-dealer-delhi" className="text-sm text-gray-700 hover:text-blue-600 block">Chillers</Link></li>
                    </ul>
                  </div>

                  {/* Column 3: Regions */}
                  <div>
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Regions</h3>
                    <ul className="space-y-2">
                      <li><Link to="/hvac-contractor-delhi" className="text-sm text-gray-700 hover:text-blue-600 block">Delhi</Link></li>
                      <li><Link to="/hvac-contractor-gurgaon" className="text-sm text-gray-700 hover:text-blue-600 block">Gurugram (Gurgaon)</Link></li>
                      <li><Link to="/hvac-contractor-noida" className="text-sm text-gray-700 hover:text-blue-600 block">Noida</Link></li>
                      <li><Link to="#" className="text-sm text-gray-700 hover:text-blue-600 block">Greater Noida</Link></li>
                      <li><Link to="/daikin-dealer-faridabad" className="text-sm text-gray-700 hover:text-blue-600 block">Faridabad</Link></li>
                      <li><Link to="#" className="text-sm text-gray-700 hover:text-blue-600 block">Ghaziabad</Link></li>
                    </ul>
                  </div>
                </div>

                {/* Bottom Full-Width Clearance Banner */}
                <div className="bg-red-50 rounded-b-xl border-t border-red-100">
                  <Link to="/clearance-ac-sale" className="flex items-center justify-between px-6 py-4 hover:bg-red-100 transition-colors group/sale rounded-b-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-600 group-hover/sale:bg-red-600 group-hover/sale:text-white transition-colors">
                        <Tag size={16} />
                      </div>
                      <div>
                        <p className="text-red-700 font-bold text-sm">Clearance Sale</p>
                        <p className="text-red-500 text-xs font-medium">High Discount AC Deals on Old Unused Sealed Stock</p>
                      </div>
                    </div>
                    <div className="text-red-600 flex items-center gap-1 text-sm font-bold group-hover/sale:translate-x-1 transition-transform">
                      View Deals <ArrowRight size={16} />
                    </div>
                  </Link>
                </div>

              </div>
            </div>
          </nav>

          {/* Desktop CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
             <CartButton />
            <a
              href="tel:+917703900307"
              onClick={handleCallClick}
              className="inline-flex whitespace-nowrap items-center gap-2 px-3 py-2 rounded-md border-2 border-blue-600 text-blue-600 font-medium hover:bg-blue-50 transition-all duration-200 text-sm"
            >
              <Phone size={15} />
              Call Us
            </a>
            <Button
              onClick={() => { setMobileMenuOpen(false); navigate('/contact'); }}
              className="bg-blue-600 hover:bg-blue-700 text-white transition-all duration-300"
            >
              Get in Touch
            </Button>
          </div>

          <div className="lg:hidden flex items-center gap-2">
            {isShopPath(location.pathname) && <HelpMenu onCall={handleCallClick} />}
            <CartButton className="!gap-1.5 !px-2 !py-1.5" />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-700 hover:text-blue-600 transition-colors"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg max-h-[85vh] overflow-y-auto">
          <div className="px-4 py-6 space-y-4">
            {navItems.filter((item) => ['home', 'prices'].includes(item.id)).map((item) =>
              item.link ? (
                <Link
                  key={item.id}
                  to={item.link}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full text-left text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 py-2"
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="block w-full text-left text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 py-2"
                >
                  {item.label}
                </button>
              )
            )}

            {/* Compare sits with Buy AC on phones too (Clarity, Oct 2026). */}
            <Link to="/compare-ac" onClick={() => setMobileMenuOpen(false)}
              className="block w-full text-left text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 py-2 pl-3 border-l-2 border-blue-200">
              Compare ACs
            </Link>

            {/* The two corners */}
            <div className="rounded-xl border border-gray-200 p-3">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Customer Corner</p>
              <ul className="mt-1">
                {CUSTOMER_CORNER.map((it) => (
                  <li key={it.label}>
                    <Link to={it.to || '/'} onClick={(e) => mobileGo(e, it)} className="block rounded-lg py-2">
                      <span className="block font-medium text-gray-800">{it.label}</span>
                      <span className="block text-xs text-gray-500">{it.text}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl bg-blue-50 p-3 ring-1 ring-blue-200">
              <Link to="/architects-interior-designers" onClick={() => setMobileMenuOpen(false)} className="text-[11px] font-bold uppercase tracking-wider text-blue-800">Architect Corner ›</Link>
              <ul className="mt-1">
                {ARCHITECT_CORNER.map((it) => (
                  <li key={it.label}>
                    <Link to={it.to} onClick={(e) => mobileGo(e, it)} className="block rounded-lg py-2">
                      <span className="block font-medium text-gray-900">{it.label}</span>
                      <span className="block text-xs text-gray-600">{it.text}</span>
                    </Link>
                  </li>
                ))}
              </ul>
              <Link to={PARTNER_LINK} onClick={(e) => mobileGo(e, { to: PARTNER_LINK })}
                className="mt-2 flex h-10 items-center justify-center rounded-lg bg-blue-700 text-sm font-bold text-white">Become a partner</Link>
            </div>

            {navItems.filter((item) => !['home', 'prices'].includes(item.id)).map((item) =>
              item.link ? (
                <Link
                  key={item.id}
                  to={item.link}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full text-left text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 py-2"
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="block w-full text-left text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 py-2"
                >
                  {item.label}
                </button>
              )
            )}

            {/* Mobile SEO Directory (Collapsible Accordion) */}
            <details className="group border-t border-gray-100 pt-2 mt-2">
              <summary className="flex items-center justify-between w-full text-left text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 py-2 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                <span>Explore</span>
                <ChevronDown size={16} className="transform transition-transform group-open:rotate-180" />
              </summary>
              
              <div className="pl-4 mt-3 space-y-6 border-l-2 border-blue-100 pb-2 mb-2">
                {/* Brands */}
                <div>
                  <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">Brands</h3>
                  <ul className="space-y-2.5">
                    <li><Link to="/daikin-dealer-delhi" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-blue-600 block">Daikin</Link></li>
                    <li><Link to="/o-general-dealer-delhi" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-blue-600 block">O-General</Link></li>
                    <li><Link to="#" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-blue-600 block">Mitsubishi Heavy</Link></li>
                    <li><Link to="#" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-blue-600 block">Hitachi</Link></li>
                  </ul>
                </div>

                {/* Solutions */}
                <div>
                  <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">Solutions</h3>
                  <ul className="space-y-2.5">
                    <li><Link to="/vrv-ac-installation" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-blue-600 block">VRV / VRF</Link></li>
                    <li><Link to="/cassette-ac-installation" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-blue-600 block">Cassette AC</Link></li>
                    <li><Link to="/ductable-ac-installation" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-blue-600 block">Ductable AC</Link></li>
                    <li><Link to="/split-ac-installation" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-blue-600 block">Split AC</Link></li>
                    <li><Link to="/daikin-chiller-dealer-delhi" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-blue-600 block">Chillers</Link></li>
                  </ul>
                </div>

                {/* Regions */}
                <div>
                  <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">Regions</h3>
                  <ul className="space-y-2.5">
                    <li><Link to="/hvac-contractor-delhi" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-blue-600 block">Delhi</Link></li>
                    <li><Link to="/hvac-contractor-gurgaon" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-blue-600 block">Gurugram</Link></li>
                    <li><Link to="/hvac-contractor-noida" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-blue-600 block">Noida</Link></li>
                    <li><Link to="/daikin-dealer-faridabad" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-blue-600 block">Faridabad</Link></li>
                  </ul>
                </div>
              </div>
            </details>

            {/* Mobile Clearance Banner */}
            <Link 
              to="/clearance-ac-sale" 
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-between bg-red-50 border border-red-100 rounded-lg p-3 mt-4 hover:bg-red-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Tag size={18} className="text-red-600" />
                <div>
                  <p className="text-red-700 font-bold text-sm">Clearance Sale</p>
                  <p className="text-red-500 text-xs font-medium">Unused Old Stock Sealed-Box ACs</p>
                </div>
              </div>
              <ArrowRight size={18} className="text-red-600" />
            </Link>

            <a
              href="tel:+917703900307"
              onClick={handleCallClick}
              className="flex items-center justify-center gap-2 w-full py-2 mt-4 rounded-md border-2 border-blue-600 text-blue-600 font-medium hover:bg-blue-50 transition-all"
            >
              <Phone size={16} />
              Call Us
            </a>
            <Button
              onClick={() => { setMobileMenuOpen(false); navigate('/contact'); }}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white transition-all duration-300"
            >
              Get in Touch
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};